import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchShopifyProducts, ShopifyProduct } from '@/lib/shopify';
import { useCartStore } from '@/stores/cartStore';
import { useInventoryOverrides } from '@/hooks/useInventoryOverrides';
import { toast } from 'sonner';

// Priority handles first (spray, popular grips, accessories), then anything else in stock
const PRIORITY = ['grip', 'spray', 'towel', 'overgrip', 'tacky', 'dry'];

const score = (p: ShopifyProduct) => {
  const t = `${p.node.handle} ${p.node.title}`.toLowerCase();
  if (t.includes('spray') || t.includes('grip-pro')) return 0;
  const i = PRIORITY.findIndex((k) => t.includes(k));
  return i === -1 ? 99 : i + 1;
};

const CartRecommendations = () => {
  const { items, addItem, isLoading } = useCartStore();
  const { data: overrides } = useInventoryOverrides();
  const [adding, setAdding] = useState<string | null>(null);

  const { data: products = [] } = useQuery({
    queryKey: ['cart-recommendations'],
    queryFn: () => fetchShopifyProducts(40),
    staleTime: 10 * 60 * 1000,
  });

  const recs = useMemo(() => {
    const inCart = new Set(items.map((i) => i.product.node.handle));
    return products
      .filter((p) => !inCart.has(p.node.handle))
      .filter((p) => overrides?.get(p.node.handle)?.override_status !== 'force_sold_out')
      .filter((p) => p.node.variants.edges.some((v) => v.node.availableForSale))
      .sort((a, b) => score(a) - score(b))
      .slice(0, 6);
  }, [products, items, overrides]);

  if (recs.length === 0) return null;

  const handleAdd = async (p: ShopifyProduct) => {
    const variant = p.node.variants.edges.find((v) => v.node.availableForSale)?.node;
    if (!variant) return;
    setAdding(p.node.id);
    try {
      await addItem({
        product: p,
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        quantity: 1,
        selectedOptions: variant.selectedOptions,
      });
      toast.success(`Added ${p.node.title}!`, { position: 'top-center' });
    } catch {
      toast.error('Failed to add item. Please try again.');
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-bold uppercase tracking-wider text-foreground">
        {items.length ? 'Complete Your Game' : 'Popular Picks'}
      </p>
      <div className="-mx-1 flex snap-x snap-mandatory gap-2 overflow-x-auto px-1 pb-1">
        {recs.map((p) => {
          const img = p.node.images.edges[0]?.node;
          const price = parseFloat(p.node.priceRange.minVariantPrice.amount);
          return (
            <div
              key={p.node.id}
              className="flex w-32 shrink-0 snap-start flex-col rounded-lg border border-border bg-card p-2"
            >
              {img && (
                <img
                  src={img.url}
                  alt={img.altText || p.node.title}
                  loading="lazy"
                  className="mb-1.5 aspect-square w-full rounded object-contain bg-muted"
                />
              )}
              <p className="line-clamp-2 text-[11px] font-medium leading-tight text-foreground">{p.node.title}</p>
              <div className="mt-auto flex items-center justify-between pt-1.5">
                <span className="text-xs font-bold text-foreground">${price.toFixed(2)}</span>
                <Button
                  size="sm"
                  className="h-7 rounded-full px-2 text-[11px]"
                  onClick={() => handleAdd(p)}
                  disabled={adding === p.node.id || isLoading}
                  aria-label={`Add ${p.node.title} to cart`}
                >
                  {adding === p.node.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Plus className="h-3 w-3" />Add</>}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CartRecommendations;
